"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { apiFetch, getAccessToken } from "@/lib/api-client";

interface UserConfig {
  fontSize?: number;
}

interface UserConfigContextType {
  config: UserConfig;
  updateConfig: (updates: Partial<UserConfig>) => Promise<void>;
  isLoading: boolean;
}

const UserConfigContext = createContext<UserConfigContextType | undefined>(undefined);

export function UserConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<UserConfig>({ fontSize: 1 });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!getAccessToken()) {
      setIsLoading(false);
      return;
    }
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await apiFetch("/api/v1/users/me/config");
      if (response.ok) {
        const data = await response.json();
        setConfig(data.data);
      }
    } catch (error) {
      console.error("Failed to load user config:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateConfig = async (updates: Partial<UserConfig>) => {
    try {
      const newConfig = { ...config, ...updates };
      const response = await apiFetch("/api/v1/users/me/config", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error("Failed to update config");
      }

      const data = await response.json();
      setConfig(data.data);
    } catch (error) {
      console.error("Failed to update user config:", error);
      throw error;
    }
  };

  return (
    <UserConfigContext.Provider value={{ config, updateConfig, isLoading }}>
      {children}
    </UserConfigContext.Provider>
  );
}

export function useUserConfig() {
  const context = useContext(UserConfigContext);
  if (context === undefined) {
    throw new Error("useUserConfig must be used within a UserConfigProvider");
  }
  return context;
}
